import * as React from 'react';
import { useMemo, useState } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps as InjectedIntlProps } from 'react-intl';
import { Loader } from 'semantic-ui-react';

import { RowColor } from '../../../components/Table/Table';
import TablePagination from '../../../components/Table/TablePagination';
import { localeData } from '../../../locales/data';
import { DatasetField, DatasetFieldsResponse } from '../../../shared/api/api';

import { useWatch } from 'react-hook-form';

import { UseQueryResult } from 'react-query';
import { FilterPaginationResult, useFilterSortOrder } from '../Filters/auxiliaryFields';
import { DATASET_NAME_TABLE_COLUMN, getReactTableColumns } from './DatasetFieldsListTableColumns';
import { getColumnTransform } from '../DataTableCommon/DataTableCommon';
import { getDataFieldPath } from '../dataExplorerRouterPaths';
import isEqual from 'lodash/isEqual';
import { filterFields } from '../Filters/FilterFields/dataExplorerFieldsConfig';
import { useDataExplorerTrackCustomEvent } from '../Filters/dataExplorerAnalytics';
import { FiltersControl } from '../Filters/types';
import { useDataExplorerHistory } from '../dataExplorerHistoryContext';
import { flexRender, getCoreRowModel, Row, SortingState, useReactTable } from '@tanstack/react-table';
import { getHeaderClassName } from '../../../components/Learn/LearnBlocks/BlockTable';
import { getFooterGroups } from '../../../shared/table/table-helpers';

type Props = {
  result: UseQueryResult<DatasetFieldsResponse, Error>;
  control: FiltersControl;
  datasetId?: string;
  filterPagination: FilterPaginationResult;
  isSearchActive: boolean;
  isPyramidMultiplierVisible: boolean;
} & InjectedIntlProps;

const DatasetFieldsListTableWithoutIntl = ({
  intl,
  result,
  control,
  filterPagination,
  datasetId,
  isSearchActive,
  isPyramidMultiplierVisible,
}: Props) => {
  const history = useDataExplorerHistory();

  const { isFetching, isError, data } = result;
  const { sortOrder, handleOrderChange, isOrderDisabled } = useFilterSortOrder({ control, isSearchActive });
  const transformColumn = React.useMemo(
    () => getColumnTransform<DatasetField>({ intl, sortOrder, handleOrderChange, isOrderDisabled }),
    [intl, sortOrder, isOrderDisabled]
  );
  const [region, delay] = useWatch({ control, name: [filterFields.region.name, filterFields.delay.name] });
  const trackEvent = useDataExplorerTrackCustomEvent();

  const tableColumns = React.useMemo(() => {
    const columns = getReactTableColumns(trackEvent, isPyramidMultiplierVisible);
    const hiddenColumns = new Set([DATASET_NAME_TABLE_COLUMN]);
    return datasetId ? columns.filter(column => !hiddenColumns.has(column.id)).map(transformColumn) : columns.map(transformColumn);
  }, [transformColumn, region, delay, trackEvent]);

  const { page, handlePageChange, handlePageSizeChange, pageSize } = filterPagination;

  const getTrProps = React.useCallback(
    (state: any, rowInfo?: Row<DatasetField>, column?: undefined) => {
      return {
        // @ts-ignore
        ...RowColor(rowInfo),
        onClick: (ev: React.MouseEvent<HTMLElement>) => {
          const tagName = (ev.target as HTMLElement).tagName;
          if (tagName === 'A') return;

          const fieldId = rowInfo?.original.id as string;
          const datasetId = rowInfo?.original.dataset.id as string;
          history.push(getDataFieldPath({ fieldId, datasetId }));
        },
      };
    },
    [history]
  );

  const totalPages = Math.ceil((data?.count ?? 0) / pageSize);

  const Pagination = useMemo(
    () =>
      TablePagination({
        hasButtons: false,
        numOfResults: data?.count ?? 0,
        pageSizeLimit: 50,
        pageSize: pageSize,
        page: page,
        pages: totalPages,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
      }),
    [data?.count, page, pageSize, totalPages]
  );

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    columns: tableColumns,
    data: data?.results || [],
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    enableSortingRemoval: false,
  });

  const headerGroups = table.getHeaderGroups();

  // show loader when data load for the first time
  if (isFetching && !data) {
    return (
      <div className="loader">
        <Loader active={true} inline="centered" size="massive" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="agreements__content full-height">
        <div className="agreements__content-scroll full-height">
          <div className="agreements__not-available full-height">
            <FormattedMessage id="dataExplorer.datafields.error" defaultMessage={localeData.en['dataExplorer.datafields.error']} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="data-table__container data-table__container--fields">
      <div className="agreements__content full-height">
        {data && isFetching && (
          <div className="data-table__stale-loader-container">
            <div className="data-table__stale-loader-background"></div>
            <Loader active={true} size="massive" className="data-table__stale-loader-spinner" />
          </div>
        )}
        <div className="block-table-container">
          <div className="ReactTable block-table-wrapper -highlight">
            <div className="rt-table block-table" role="grid">
              {headerGroups.map((headerGroup, i) => (
                <div key={headerGroup.id} className={headerGroups.length === 2 && i === 0 ? 'rt-thead -headerGroups' : 'rt-thead -header'}>
                  <div
                    {...{
                      key: headerGroup.id,
                      className: 'rt-tr',
                      role: 'row',
                    }}
                  >
                    {headerGroup.headers.map(header => (
                      // eslint-disable-next-line react/jsx-key
                      <div
                        {...{
                          key: header.id,
                          className: getHeaderClassName<DatasetField>(header, sorting),
                          onClick: header.column.getToggleSortingHandler(),
                          role: 'columnheader',
                          style: {
                            width: header.getSize(),
                            flex: `${header.getSize()} 0 auto`,
                          },
                        }}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div
                {...{
                  className: 'rt-tbody',
                }}
              >
                {table.getRowModel().rows.map((row, i) => (
                  <div className="rt-tr-group" role="rowgroup" key={row.id}>
                    <div
                      {...{
                        key: row.id,
                        className: `rt-tr ${i % 2 === 0 ? '-odd' : '-even'}`,
                        role: 'row',
                        ...getTrProps(undefined, row),
                      }}
                    >
                      {row.getVisibleCells().map(cell => (
                        // eslint-disable-next-line react/jsx-key
                        <div
                          {...{
                            key: cell.id,
                            // @ts-ignore
                            className: `rt-td ${cell.column.columnDef.meta?.className ?? ''}`,
                            role: 'gridcell',
                            style: {
                              flex: `${cell.column.getSize()} 0 auto`,
                              width: cell.column.getSize(),
                            },
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {getFooterGroups(table)}
            </div>
          </div>
        </div>
        <Pagination />
      </div>
    </div>
  );
};

export const DatasetFieldsListTable = injectIntl(
  React.memo(DatasetFieldsListTableWithoutIntl, (prevProps, nextProps) => isEqual(prevProps, nextProps))
);
